import { gql, useMutation } from '@apollo/client';
import {
	Alert,
	AlertDescription,
	AlertIcon,
	Box,
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
} from '@chakra-ui/react';
import { Form, Formik, useFormikContext } from 'formik';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/km';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import SelectValueFormField from '../../../components/form/SelectValueFormField';
import c from '../../../constant';
import useStore from '../../../store';
import { mappingKhmerNumber } from '../../../utils/market-development';
import CompanyField from './ews-company-field';
import CompanyTypeField from './ews-company-type-field';

const M_CREATE = gql`
	mutation create($companyId: String!, $companyType: InsuranceInstitutionsType!, $year: Float!, $templates: [EWSRatioTemplateInput!] = []) {
		createAnnuallyFinancialStatementsRatio(companyId: $companyId, companyType: $companyType, year: $year, templates: $templates)
	}
`;

export default function CreateRatioDocument({ isOpen = false, onClose = () => null, onCallback = () => null }) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const [message, setMessage] = useState(null);
	const [createRatioDocument, { loading: loadingCreate }] = useMutation(M_CREATE);

	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				companyType: Yup.string().required('Required'),
				companyId: Yup.string().required('Required'),
				year: Yup.string().required('Required'),
			}),
		[],
	);

	const FormOnChangeHandler = ({ onChange }) => {
		const { values, isSubmitting } = useFormikContext();
		const prevValuesRef = useRef(values);

		useEffect(() => {
			if (isSubmitting) return;
			if (JSON.stringify(prevValuesRef?.current) !== JSON.stringify(values)) {
				prevValuesRef.current = values;
				onChange(values);
			}
		}, [values, isSubmitting, onChange]);

		return null;
	};

	return (
		<Modal isOpen={isOpen} closeOnEsc={loadingCreate ? false : true} closeOnOverlayClick={loadingCreate ? false : true} onClose={onClose} size="xl">
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius}>
				<ModalHeader>{t('New Document')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{message && (
						<Alert mb={4} status="error">
							<AlertIcon />
							<AlertDescription>{t(message)}</AlertDescription>
						</Alert>
					)}
					<Formik
						validationSchema={validationSchema}
						initialValues={{
							companyType: '',
							companyId: '',
							year: '',
						}}
						onChange={(e) => {
							console.log('changing', e);
						}}
						onSubmit={async (values) => {
							createRatioDocument({
								variables: {
									companyType: values?.companyType,
									companyId: values?.companyId,
									year: parseInt(values?.year),
								},
							})
								.then(({ data }) => {
									onCallback({
										id: data?.createAnnuallyFinancialStatementsRatio,
										year: values?.year,
										companyType: values?.companyType,
										companyId: values?.companyId,
									});
									onClose();
								})
								.catch((error) => {
									setMessage(error?.message || 'something went wrong');
								});
						}}
					>
						{({ values, ...props }) => {
							return (
								<Box pb="4">
									<Form>
										<FormOnChangeHandler onChange={(values) => setMessage(null)} />
										<SimpleGrid columns={1} spacing={4}>
											<CompanyTypeField
												{...props}
												name="companyType"
												companyFormName="companyId"
												label="Company Type"
												placeholder={t('Company Type')}
											/>
											<CompanyField {...props} name="companyId" companyTypeFormName="companyType" label="Company" />
											<SelectValueFormField
												{...props}
												placeholder={t('Year')}
												name="year"
												label="Year"
												options={[0].reduce((p, c) => {
													let lp = p;
													let i = 2021;
													while (i <= moment().year()) {
														lp.push({ label: lang === 'kh' ? mappingKhmerNumber(i) : i, value: i });
														i = i + 1;
													}
													return lp?.reverse();
												}, [])}
											/>
											<Flex mt={2} justifyContent="flex-end">
												<Button
													ml={2}
													colorScheme="blue"
													variant="solid"
													size="md"
													h="42px"
													w="128px"
													type="submit"
													borderRadius={c.borderRadius}
													loading={loadingCreate}
												>
													{t('Create')}
												</Button>
											</Flex>
										</SimpleGrid>
									</Form>
								</Box>
							);
						}}
					</Formik>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
